/**
 * @file report.model.ts
 * @description Core data model interfaces and helper functions for the Kirigami report system.
 *
 * Pure data shapes (Section, Question, CrossSectionRelations, UnitReport, Feedback,
 * AnswerFeedback, ScoreLevel, FinalResult) are sourced from `@kirigami/domain/analysis-types`
 * and re-exported from here for backwards compatibility with existing consumers.
 *
 * View-model concerns (`_uid`, `stage`, `priority`, `status`, `sectionName`, `_cardCollapsed`,
 * `_dismissing`, `overallScore`, etc.) are app-specific and live locally — they intersect
 * the canonical lib types rather than replacing them.
 *
 * Scoring system:
 * - Scores range from 0–10
 * - Stages (metamorphosis metaphor): Egg (0–1), Caterpillar (2–4), Chrysalis (5–7), Butterfly (8–10)
 * - Priorities: High (0–3), Medium (4–6), Low (7–10)
 *
 * @see report.service.ts — processes raw JSON into these interfaces
 * @see Vania_TO_DO.md — backend requests to make stage/priority thresholds configurable
 */

import type {
  Section as LibSection,
  Question as LibQuestion,
  UnitReport as LibUnitReport,
  CrossSectionRelations as LibCrossSectionRelations,
  Feedback as LibFeedback,
  ScoreLevel as LibScoreLevel,
  FinalResult as LibFinalResult,
  ReportAggregate as LibReportAggregate,
} from '@kirigami/domain/analysis-types';

// Re-export purely canonical lib types that the app does not extend. Types that
// the app extends with view-model fields (Section, Question, UnitReport, CrossSectionRelations)
// are declared locally further down to preserve the existing `Report` / `Relation` shape.
export type {
  Feedback,
  AnswerFeedback,
  ScoreLevel,
  FinalResult,
} from '@kirigami/domain/analysis-types';

/** Alias of the lib's `ScoreLevel` used by view code. */
export type DomainScoreLevel = LibScoreLevel;
/** Alias of the lib's `FinalResult` used by view code. */
export type DomainFinalResult = LibFinalResult;

// ── Feedback ────────────────────────────────────────

/**
 * A single feedback item from the analysis engine.
 * Used in Content analysis (via Question.feedback) and Sections analysis (via Section.feedback).
 *
 * Extends the canonical `Feedback` shape from `@kirigami/domain/analysis-types`
 * with view-model fields (_uid, stage, priority, status, sectionName) and runtime
 * UI flags (_cardCollapsed, _dismissing) that the backend does not know about.
 */
export interface FeedbackItem extends LibFeedback {
  /** Feedback category label (e.g., "Missing Information", "Incomplete Content", "Lack of Clarity") */
  category: string;
  /** What's wrong — the identified issue in the paper */
  problem: string;
  /** How to fix it — the suggested improvement */
  solution: string;
  /** Quality score from 0 (worst) to 10 (best) */
  score: number;

  // ── Derived fields (set by ReportService.enrichFeedback(), not from JSON) ──

  /** Unique identifier assigned during data loading (e.g., "fb-0", "fb-42"). Used for Action Plan click-to-navigate. */
  _uid?: string;
  /** Metamorphosis stage derived from score via getStage() */
  stage?: 'egg' | 'caterpillar' | 'chrysalis' | 'butterfly';
  /** Urgency level derived from score via getPriority() */
  priority?: 'high' | 'medium' | 'low';
  /** User action status — tracks review progress */
  status?: 'pending' | 'done' | 'ignored';
  /** Parent section name — set during processSections() for filtering/display */
  sectionName?: string;

  // ── Runtime UI state (set by components, not from JSON or service) ──

  /** Whether the feedback card body is collapsed in the detail view. Defaults to true. */
  _cardCollapsed?: boolean;
  /** Dismissal animation state. Set briefly during slide-out animation. */
  _dismissing?: 'done' | 'ignored' | null;
}

// ── Questions (Content Analysis) ────────────────────

/**
 * A content analysis question with its feedback items.
 * Each question targets a specific section of the paper (via sectionId)
 * and contains multiple FeedbackItem responses.
 *
 * Extends the canonical `Question` shape from `@kirigami/domain/analysis-types`
 * with app-specific fields (`id`, required sectionId, typed FeedbackItem[] feedback, UI state).
 */
export interface Question extends Omit<LibQuestion, 'sectionId' | 'feedback'> {
  /** Unique question identifier from the analysis engine */
  id: string;
  /** Section this question applies to (e.g., "introduction", "subresult_1"). Normalized via ReportService.normalizeSectionId(). */
  sectionId: string;
  /** Machine-readable question name (e.g., "relevance", "clarity") */
  question_name: string;
  /** Human-readable question text displayed as the group card title */
  question: string;
  /** The author's text that was analysed (shown under "WHAT WE FOUND IN YOUR DOCUMENT") */
  answer: string;
  /** Feedback items for this question, sorted by score */
  feedback: FeedbackItem[];

  // ── Runtime UI state ──

  /** Whether the "WHAT WE FOUND" text block is collapsed. Defaults to true. */
  _collapsed?: boolean;
}

// ── Units (Knowledge Units) ─────────────────────────

/**
 * A knowledge unit within a section — represents a discrete piece of information
 * that the analysis engine identified in the paper.
 *
 * Extends the canonical `UnitReport` shape from `@kirigami/domain/analysis-types`
 * with app-specific required fields and the local `FeedbackItem` view-model.
 */
export interface UnitReport extends Omit<
  LibUnitReport,
  'index' | 'unit_name' | 'unit_present' | 'unit_content' | 'feedback'
> {
  /** Position index within the section */
  index: number;
  /** Unique unit identifier */
  id: string;
  /** Parent section identifier */
  sectionId: string;
  /** Descriptive name of the knowledge unit */
  unit_name: string;
  /** Whether this unit was found present in the paper */
  unit_present: boolean;
  /** The actual text content of this unit */
  unit_content: string;
  /** Feedback items specific to this unit */
  feedback: FeedbackItem[];
  /** Relations from this unit to other units */
  unitsToRelation: unknown[];
  /** Relations from this unit to sections */
  sectionsToRelation: unknown[];
}

// ── Sections ────────────────────────────────────────

/**
 * A section of the analysed paper (e.g., Title, Abstract, Introduction, Results).
 *
 * Sub-sections (e.g., Subresult 1, Subresult 2) are identified by the `section_type` field,
 * which contains the lowercase name of their parent section (e.g., "results").
 * The UI nests these under their parent in the sidebar navigation.
 *
 * Extends the canonical `Section` shape from `@kirigami/domain/analysis-types`
 * with app-specific required fields and the local `FeedbackItem` / `UnitReport` view-models.
 */
export interface Section extends Omit<
  LibSection,
  'section_name' | 'section_content' | 'units_report' | 'feedback'
> {
  /** Display name of the section (e.g., "Title", "Abstract", "Subresult 1") */
  section_name: string;
  /** Full text content of this section from the paper */
  section_content: string;
  /** Parent section type for sub-sections (e.g., "results" means this nests under "Results"). Undefined for top-level sections. */
  section_type?: string;
  /** Knowledge units identified within this section */
  units_report: UnitReport[];
  /** Structure/formatting feedback for this section (used in Sections tab) */
  feedback?: FeedbackItem[];
}

// ── Relations (Coherence Analysis) ──────────────────

/**
 * A coherence relation between two sections of the paper.
 * Evaluates how well ideas connect across sections (e.g., claims in Introduction
 * supported by evidence in Results).
 *
 * Relations with `problem: 'None'` are filtered out from the UI — they indicate
 * no coherence issue was found for that pair.
 *
 * Extends the canonical `CrossSectionRelations` shape from
 * `@kirigami/domain/analysis-types` with app-specific view-model fields.
 */
export interface Relation extends Omit<LibCrossSectionRelations, 'related_units'> {
  /** Position index in the relations array */
  index: number;
  /** Source knowledge unit identifier */
  unit_from: string;
  /** Source section name (e.g., "introduction") — normalized via normalizeSectionId() */
  section_from: string;
  /** Target knowledge unit identifier */
  unit_to: string;
  /** Target section name (e.g., "discussion") — normalized via normalizeSectionId() */
  section_to: string;
  /** Human-readable description of the expected relationship */
  description: string;
  /** Whether the expected relation was found in the paper */
  relation_found: boolean;
  /** Relation category (e.g., "Scope Creep", "Logical Misalignment") */
  category: string;
  /** Coherence quality score from 0 (worst) to 10 (best) */
  score: number;
  /** What's wrong — the coherence issue identified. "None" if no issue. */
  problem: string;
  /** How to fix it — the suggested improvement */
  solution: string;
  /** Related knowledge units involved in this relation */
  related_units: unknown[];

  // ── Derived fields (set by ReportService, not from JSON) ──

  /** Unique identifier (e.g., "rel-0", "rel-15"). Used for Action Plan click-to-navigate. */
  _uid?: string;
  /** Metamorphosis stage derived from score */
  stage?: 'egg' | 'caterpillar' | 'chrysalis' | 'butterfly';
  /** Urgency level derived from score */
  priority?: 'high' | 'medium' | 'low';
  /** User action status */
  status?: 'pending' | 'done' | 'ignored';

  // ── Runtime UI state ──

  /** Whether the relation card body is collapsed. Defaults to true. */
  _cardCollapsed?: boolean;
  /** Dismissal animation state. Set briefly during slide-out animation. */
  _dismissing?: 'done' | 'ignored' | null;
}

// ── Report (Root) ───────────────────────────────────

/**
 * The root report object used inside the app.
 *
 * Intersects the canonical `ReportAggregate` shape from `@kirigami/domain/analysis-types`
 * (which represents a fully-hydrated report returned by the backend) with the app's
 * view-model specific overrides — required `stage`/`report_id`/`status`, the local
 * `Section` / `Question` / `Relation` arrays with enriched feedback, plus the computed
 * scores that `ReportService.processReport()` derives on the client.
 */
export type Report = Omit<
  LibReportAggregate,
  'sections' | 'questions' | 'relations' | 'status' | 'title' | 'description' | 'processing_status'
> & {
  /** Analysis pipeline stage (e.g., "finalResult") */
  stage: string;
  /** Unique report identifier (UUID) */
  report_id: string;
  /** Processing status (e.g., "processing", "succeeded", "failed") */
  status: string;
  /** All paper sections including sub-sections */
  sections: Section[];
  /** Content analysis questions with feedback */
  questions: Question[];
  /** Coherence relations between sections */
  relations: Relation[];
  /** Explicit paper title from the backend (if available). Fallback chain in ReportService.extractPaperTitle(). */
  paper_title?: string;

  // ── Computed by ReportService.processReport() ──

  /** Overall paper score: average of contentScore + sectionScore + coherenceScore, rounded to 1 decimal */
  overallScore?: number;
  /** Average score across all Content (question) feedback items */
  contentScore?: number;
  /** Average score across all Sections (structure) feedback items */
  sectionScore?: number;
  /** Average score across all Coherence (relation) scores */
  coherenceScore?: number;
};

// ── Helper Functions ────────────────────────────────

/**
 * Maps a numeric score (0–10) to a metamorphosis stage.
 *
 * Thresholds are currently hardcoded. See Vania_TO_DO.md for the plan
 * to make these configurable via `stage_thresholds` from the backend.
 *
 * @param score - Quality score from 0 to 10
 * @returns The corresponding metamorphosis stage
 *
 * @example
 * getStage(0)   // 'egg'
 * getStage(3)   // 'caterpillar'
 * getStage(5.5) // 'chrysalis'
 * getStage(9)   // 'butterfly'
 */
export function getStage(score: number): 'egg' | 'caterpillar' | 'chrysalis' | 'butterfly' {
  if (score <= 1) return 'egg';
  if (score <= 4) return 'caterpillar';
  if (score <= 7) return 'chrysalis';
  return 'butterfly';
}

/**
 * Maps a numeric score (0–10) to a priority level.
 *
 * Lower scores = higher priority (more urgent issues).
 * Thresholds are currently hardcoded. See Vania_TO_DO.md for the plan
 * to receive `priority` directly from the backend.
 *
 * @param score - Quality score from 0 to 10
 * @returns The corresponding priority level
 *
 * @example
 * getPriority(1) // 'high'
 * getPriority(5) // 'medium'
 * getPriority(8) // 'low'
 */
export function getPriority(score: number): 'high' | 'medium' | 'low' {
  if (score <= 3) return 'high';
  if (score <= 6) return 'medium';
  return 'low';
}

/**
 * Returns the primary CSS colour variable for a metamorphosis stage.
 * Used for text, icons, and score values.
 *
 * @param stage - The metamorphosis stage name
 * @returns CSS custom property string (e.g., "var(--coral-500)")
 */
export function getStageColor(stage: string): string {
  switch (stage) {
    case 'egg':
      return 'var(--purple-600)';
    case 'caterpillar':
      return 'var(--pink-500)';
    case 'chrysalis':
      return 'var(--coral-500)';
    case 'butterfly':
      return 'var(--tree-500)';
    default:
      return 'var(--neu-400)';
  }
}

/**
 * Returns the light background CSS colour variable for a metamorphosis stage.
 * Used for badges, icon backgrounds, and subtle fills.
 *
 * @param stage - The metamorphosis stage name
 * @returns CSS custom property string (e.g., "var(--coral-200)")
 */
export function getStageBgColor(stage: string): string {
  switch (stage) {
    case 'egg':
      return 'var(--purple-200)';
    case 'caterpillar':
      return 'var(--pink-200)';
    case 'chrysalis':
      return 'var(--coral-200)';
    case 'butterfly':
      return 'var(--tree-200)';
    default:
      return 'var(--neu-100)';
  }
}

/**
 * Returns the border/accent CSS colour variable for a metamorphosis stage.
 * Used for card left borders and accent lines.
 *
 * @param stage - The metamorphosis stage name
 * @returns CSS custom property string (e.g., "var(--coral-400)")
 */
export function getStageBorderColor(stage: string): string {
  switch (stage) {
    case 'egg':
      return 'var(--purple-400)';
    case 'caterpillar':
      return 'var(--pink-400)';
    case 'chrysalis':
      return 'var(--coral-400)';
    case 'butterfly':
      return 'var(--tree-400)';
    default:
      return 'var(--neu-200)';
  }
}
